<script setup lang="ts">
import { computed, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { MaintenanceLanguage } from "../../types/TextMaintenanceTypes";

const props = defineProps({
    languages: {
        type: Array as () => MaintenanceLanguage[],
        required: true,
    },
    maxSelection: {
        type: Number,
        default: 2,
    },
    hasLanguageChanges: {
        type: Function as PropType<(languageCode: string) => boolean>,
        required: true,
    },
});

const emit = defineEmits(["language-select"]);

const { t } = useI18n();

const selectedLanguages = computed(() => 
    props.languages.filter(lang => lang.selected).map(lang => lang.code)
);

// Function to get translated language name
const getLanguageName = (languageCode: string): string => {
    const translationKey = `languageNames.${languageCode.toLowerCase()}`;
    const translatedName = t(translationKey);
    
    // If translation exists and is not the same as the key, use it
    // Otherwise fallback to the original name or uppercase code
    return translationKey === translatedName ? languageCode.toUpperCase() : translatedName;
};

// Function to get translated language name with asterisk if changed
const getLanguageLabel = (languageCode: string): string => {
    const name = getLanguageName(languageCode);
    const hasChanges = props.hasLanguageChanges(languageCode);
    return hasChanges ? `${name} *` : name;
};

// Sort languages alphabetically by translated name
const sortedLanguages = computed(() => {
    return [...props.languages].sort((a, b) => {
        const nameA = getLanguageName(a.code);
        const nameB = getLanguageName(b.code);
        return nameA.localeCompare(nameB);
    });
});

const handleLanguageClick = (language: MaintenanceLanguage) => {
    if (language.disabled) return;
    
    language.selected = !language.selected;
    
    // Update disabled state for other languages
    const currentSelected = props.languages.filter(lang => lang.selected);
    
    if (currentSelected.length >= props.maxSelection) {
        // Disable unselected languages
        for (const lang of props.languages) {
            if (!lang.selected) {
                lang.disabled = true;
            }
        }
    } else {
        // Enable all languages
        for (const lang of props.languages) {
            lang.disabled = false;
        }
    }
    
    // Emit the selected languages
    emit("language-select", selectedLanguages.value);
};

const getButtonClass = (language: MaintenanceLanguage) => {
    const baseClasses = "p-button-sm mr-2 mb-2 language-button-styled";
    
    if (language.selected) {
        return `${baseClasses} language-selected`;
    } else if (language.disabled) {
        return `${baseClasses} language-disabled`;
    } else {
        return `${baseClasses} language-unselected`;
    }
};
</script>

<template>
    <div class="language-button-group">
        <div class="flex flex-wrap">
            <Button
                v-for="language in sortedLanguages"
                :key="language.code"
                :label="getLanguageLabel(language.code)"
                :class="getButtonClass(language)"
                :severity="'secondary'"
                :disabled="language.disabled"
                :outlined="!language.selected && !language.disabled"
                :data-has-changes="hasLanguageChanges(language.code)"
                @click="handleLanguageClick(language)"
                size="small"
                :data-cy="`text-maintenance_language-${language.code}`"
            />
        </div>
        
        <!-- Selection Info -->
        <div v-if="selectedLanguages.length > 0" class="mt-2 text-sm text-content-muted">
            {{ selectedLanguages.length }} / {{ maxSelection }} {{ t('textMaintenance.languagesSelected') }}
        </div>
    </div>
</template>

<style scoped>
.language-button-group {
    min-height: 3rem;
}

/* Base styling for all language buttons */
.language-button-styled {
    transition: all 0.2s ease;
    font-weight: 500;
}

/* Selected language buttons - Dark blue background with white text */
.language-selected {
    background-color: var(--p-secondary-500) !important;
    border-color: var(--p-secondary-500) !important;
    color: white !important;
}

.language-selected:hover:not(:disabled) {
    background-color: var(--p-secondary-600) !important;
    border-color: var(--p-secondary-600) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Unselected language buttons - Outlined with secondary color */
.language-unselected {
    background-color: transparent !important;
    border-color: var(--p-secondary-500) !important;
    color: var(--p-secondary-500) !important;
}

.language-unselected:hover:not(:disabled) {
    background-color: var(--p-secondary-50) !important;
    border-color: var(--p-secondary-600) !important;
    color: var(--p-secondary-600) !important;
    transform: translateY(-1px);
}

/* Disabled language buttons */
.language-disabled {
    background-color: var(--p-surface-200) !important;
    border-color: var(--p-surface-300) !important;
    color: var(--p-surface-400) !important;
    opacity: 0.6;
    cursor: not-allowed !important;
}

.language-disabled:hover {
    transform: none !important;
    box-shadow: none !important;
}

/* Asterisk styling for changed languages */
:deep(.p-button-label) {
    display: flex;
    align-items: center;
}

/* Style for buttons with changes (containing asterisk) */
:deep(.p-button[data-has-changes="true"] .p-button-label) {
    font-weight: 700;
}

/* Orange tint for changed language buttons */
:deep(.p-button[data-has-changes="true"]:not(.language-disabled)) {
    border-color: var(--p-orange-500) !important;
}

:deep(.p-button[data-has-changes="true"].language-selected) {
    box-shadow: 0 0 0 2px var(--p-orange-200) !important;
}
</style>